import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getTurnstileToken } from '../lib/turnstile';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Mail, Phone, MapPin, ArrowRight, CheckCircle, Quote } from 'lucide-react';
import SubscribeWidget from '../components/SubscribeWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service_interest: '',
    website: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const turnstile_token = await getTurnstileToken('contact');
      await axios.post(`${API_URL}/api/contact`, {
        ...formData,
        turnstile_token,
        form_type: 'contact'
      });
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Contact" description="Get in touch with DCA Enterprises. Savannah, Georgia.">
      {/* Hero Section */}
      <section className="section-padding bg-white" data-testid="contact-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="overline mb-4 inline-block">Contact</span>
            <h1 className="heading-1 text-4xl sm:text-5xl lg:text-6xl text-[#0A0F1C] mb-6" data-testid="contact-title">
              Let's <span className="text-[#1D4ED8]">Connect</span>
            </h1>
            <p className="body-text text-[#475569] text-lg">
              Have a question, inquiry, or want to discuss a potential partnership? 
              I'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="heading-3 text-xl text-[#0A0F1C] mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#EFF6FF] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-[#1D4ED8]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0A0F1C] mb-1">Email</h3>
                    <a 
                      href="mailto:info@DCAEnterprisesInc.com"
                      className="text-[#475569] hover:text-[#1D4ED8] transition-colors"
                      data-testid="contact-email"
                    >
                      info@DCAEnterprisesInc.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FFF7ED] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-[#F97316]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0A0F1C] mb-1">Phone</h3>
                    <a 
                      href="tel:912-376-9006"
                      className="text-[#475569] hover:text-[#1D4ED8] transition-colors"
                      data-testid="contact-phone"
                    >
                      912-376-9006
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#EFF6FF] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-[#1D4ED8]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0A0F1C] mb-1">Address</h3>
                    <p className="text-[#475569]" data-testid="contact-address">
                      10520 Abercorn St Ste D<br />
                      Savannah, GA 31419
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-[#1D4ED8] text-white">
                <h3 className="font-bold mb-2">Ready for a Strategy Session?</h3>
                <p className="text-sm text-[#BFDBFE] mb-4">
                  Skip the form and book a direct session with me.
                </p>
                <Link 
                  to="/schedule"
                  className="btn-primary-orange inline-flex items-center gap-2 text-sm"
                  data-testid="contact-book-cta"
                >
                  Book Session <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 border border-[#E2E8F0] rounded-lg shadow-sm">
                {!submitted ? (
                  <>
                    <h2 className="heading-3 text-xl text-[#0A0F1C] mb-6">Send a Message</h2>
                    
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      {/* Honeypot: hidden from humans, bots fill it and get flagged */}
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                        aria-hidden="true"
                      />
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-[#0A0F1C] mb-2">
                            Full Name <span className="text-red-500" aria-hidden="true">*</span>
                          </label>
                          <Input
                            id="contact-name"
                            type="text"
                            placeholder="Your name"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="form-input"
                            data-testid="contact-form-name"
                            required
                            autoComplete="name"
                            aria-required="true"
                            maxLength={120}
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-[#0A0F1C] mb-2">
                            Email <span className="text-red-500" aria-hidden="true">*</span>
                          </label>
                          <Input
                            id="contact-email"
                            type="email"
                            placeholder="you@example.com"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="form-input"
                            data-testid="contact-form-email"
                            required
                            autoComplete="email"
                            aria-required="true"
                          />
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="contact-phone" className="block text-sm font-medium text-[#0A0F1C] mb-2">Phone</label>
                          <Input
                            id="contact-phone"
                            type="tel"
                            placeholder="Your phone number"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            className="form-input"
                            data-testid="contact-form-phone"
                            autoComplete="tel"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-service" className="block text-sm font-medium text-[#0A0F1C] mb-2">Service Interest</label>
                          <select
                            id="contact-service"
                            value={formData.service_interest}
                            onChange={(e) => setFormData({...formData, service_interest: e.target.value})}
                            className="form-input w-full"
                            data-testid="contact-form-service"
                          >
                            <option value="">Select a service</option>
                            <option value="Business Jumpstart Strategy">Business Jumpstart Strategy</option>
                            <option value="Business Credibility Setup">Business Credibility Setup</option>
                            <option value="One-on-One Consulting">One-on-One Consulting</option>
                            <option value="Training & Workshops">Training & Workshops</option>
                            <option value="Partnership">Partnership Inquiry</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-[#0A0F1C] mb-2">
                          Message <span className="text-red-500" aria-hidden="true">*</span>
                        </label>
                        <Textarea
                          id="contact-message"
                          placeholder="Tell me about your inquiry..."
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          className="form-input min-h-[150px]"
                          data-testid="contact-form-message"
                          required
                          aria-required="true"
                          maxLength={4000}
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        disabled={submitting}
                        className="btn-primary-blue"
                        data-testid="contact-form-submit"
                      >
                        {submitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-12" data-testid="contact-form-success">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-green-600" />
                    </div>
                    <h3 className="heading-3 text-xl text-[#0A0F1C] mb-2">Message Sent!</h3>
                    <p className="text-[#475569] mb-6">
                      Thank you for reaching out. I'll get back to you as soon as possible.
                    </p>
                    <Link 
                      to="/"
                      className="text-[#1D4ED8] font-bold hover:underline"
                    >
                      Back to Home
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Share Your Story CTA */}
      <section className="section-padding bg-[#0F172A] text-white relative overflow-hidden" data-testid="contact-share-story-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#F97316]/15 rounded-full mb-5">
              <Quote size={24} className="text-[#F97316]" />
            </div>
            <span className="overline text-[#F97316] mb-3 inline-block">Already worked with David?</span>
            <h2 className="heading-2 text-3xl lg:text-4xl mb-4">Share your story.</h2>
            <p className="body-text text-white/75 max-w-2xl mx-auto mb-6">
              If a session, a resource, or a conversation moved your business forward, tell us in your own words. Your story helps the next entrepreneur take their first step.
            </p>
            <Link
              to="/share-your-story"
              className="btn-primary-orange inline-flex items-center gap-2"
              data-testid="contact-share-story-btn"
            >
              Share My Story <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="section-padding bg-white" data-testid="contact-subscribe-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <SubscribeWidget />
          </div>
        </div>
      </section>
    </Layout>
  );
}
